import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, firstValueFrom, map } from "rxjs";
import { SharedService } from "./shared.service";
import { environment } from "src/environments/environment.prod";
@Injectable({
    providedIn: 'root'
})
export class RockService {

  apiURL = environment.prodAPI.url;
  rockKey = environment.prodAPI.key;

  entityType = '';
  
  constructor(
      private http: HttpClient,
      private sharedService: SharedService
  ) {}

  async getOptions(): Promise<any> {
    const headers = new HttpHeaders({Accept: 'application/json', 'Authorization-Token': `${this.rockKey}`});
    const options = { headers };
    return options;
  }

  fullRequestURL(apiURL: string, resource?: string | number): string {
    let request_url = apiURL;

    if (resource) {
      if (request_url[request_url.length - 1] === '/') {
        request_url += resource;
      } else {
        request_url += '/' + resource;
      }
    }
    return request_url;
  }

  async httpRequestWithConfig(httpObservableRequest: Observable<any>): Promise<any> {
    return firstValueFrom(httpObservableRequest.pipe(
      map(res => res),
    )).then(res => {
      return this.sharedService.handleErrors(res);
    }).catch(err => err);
  }

  async get(resource: string | number): Promise<any> {
    return this.httpRequestWithConfig(
      this.http.get(this.fullRequestURL(this.apiURL, resource), await this.getOptions())
    );
  }

  async post(resource: string | number, body: any = {}): Promise<any> {
    return this.httpRequestWithConfig(
      this.http.post(this.fullRequestURL(this.apiURL, resource), body, await this.getOptions())
    );
  }

  async patch(resource: string | number, body: any = {}): Promise<any> {
    return this.httpRequestWithConfig(
      this.http.patch(this.fullRequestURL(this.apiURL, resource), body, await this.getOptions())
    );
  }

  async put(resource: string | number, body: any = {}): Promise<any> {
    return this.httpRequestWithConfig(
      this.http.put(this.fullRequestURL(this.apiURL, resource), body, await this.getOptions())
    );
  }

  async delete(resource: string | number): Promise<any> {
    return this.httpRequestWithConfig(
      this.http.delete(this.fullRequestURL(this.apiURL, resource), await this.getOptions())
    );
  }

  async options(resource: string | number): Promise<any> {
    return this.httpRequestWithConfig(
      this.http.options(this.fullRequestURL(this.apiURL, resource), await this.getOptions())
    );
  }

  async login(credentials: LoginParameters) {
    const headers = new HttpHeaders({'Content-Type': 'application/json', Accept: 'application/json'});
    const body = {
    Username: credentials.Username,
    Password: credentials.Password,
    Persisted: true
    };
    this.http.post(this.apiURL + 'Auth/Login', body, {headers, observe: 'response'}).subscribe(response => {
        console.log(response);
    });
  }

  restLogin() {
    const headers = new HttpHeaders({'Content-Type': 'application/json', Accept: 'application/json'});
    const body = {
    Authorization: this.rockKey,
    Persisted: true
    };
    this.http.post(this.apiURL + 'Auth/Login', body, {headers, observe: 'response'}).subscribe(response => {
        console.log(response);
    });
  }

  async getRestControllerNames() {
    const controllerNames = await this.get('RestControllers/RestControllerNames?includeObsolete=false');
    return controllerNames;
  }

  async getRestContoller(controllerName: string) {
    const controller = await this.get(`doc/v1?controllerName=${controllerName}`);
    console.log(controller);
    return controller;
  }

  async getConnectionRequestDetails(connectionRequest: any) {
    
    connectionRequest.ConnectionStatus = await this.getConnectionStatus(connectionRequest.ConnectionStatusId);
    connectionRequest.ConnectionRequestActivities = await this.getConnectionRequestActivities(connectionRequest.Id);
    connectionRequest.Campus = await this.getCampus(connectionRequest.CampusId);
    
    if (connectionRequest.ConnectorPersonAliasId) {
      const connectorPersonAlias = await this.getPersonAliasAndPerson(connectionRequest.ConnectorPersonAliasId);
      if (connectorPersonAlias) {
        connectionRequest.ConnectorPersonAlias = connectorPersonAlias;
      }
    }
    if (connectionRequest.PersonAliasId) {
      const personAlias = await this.getPersonAliasAndPerson(connectionRequest.PersonAliasId);
      if (personAlias) {
        const primaryFamily = await this.getPrimaryFamily(personAlias.Person.PrimaryFamilyId);
        if (primaryFamily) {
          personAlias.Person.PrimaryFamily = primaryFamily;
        }
        connectionRequest.PersonAlias = personAlias;
      }
    }
  }

  async getPersonAliasAndPerson(personAliasId: number) {
    if (!personAliasId) {
      return;
    }
    const personAlias = await this.getPersonAlias(personAliasId);
    const person = await this.getPerson(personAlias.PersonId);

    person.RecordTypeValue = await this.getDefinedValue(person.RecordTypeValueId);
    person.RecordStatusValue = await this.getDefinedValue(person.RecordStatusValueId);
    person.ConnectionStatusValue = await this.getDefinedValue(person.ConnectionStatusValueId);

    if (person.MaritalStatusValueId) {
      person.MaritalStatusValue = await this.getDefinedValue(person.MaritalStatusValueId);
    }
    if (person.TitleValueId) {
      person.TitleValue = await this.getDefinedValue(person.TitleValueId);
    }
    if (person.SuffixValueId) {
      person.SuffixValue = await this.getDefinedValue(person.SuffixValueId);
    }
    if (person.EthnicityValueId) {
      person.EthnicityValue = await this.getDefinedValue(person.EthnicityValueId);
    }
    if (person.RaceValueId) {
      person.RaceValue = await this.getDefinedValue(person.RaceValueId);
    }
    if (person.PhoneNumbers && person.PhoneNumbers.length) {
      for (const phoneNumber of person.PhoneNumbers) {
        phoneNumber.NumberTypeValue = await this.getDefinedValue(phoneNumber.NumberTypeValueId);
      }
    }

    if (person.PhotoId) {
      person.Photo = await this.getBinaryFile(person.PhotoId);
    }

    
    personAlias.Person = person;
    return personAlias;
  }

  async getPersonAlias(personAliasId: number) {
    return await this.get(`PersonAlias/${personAliasId}`);
  }

  async getPerson(personId: number) {
    return await this.get(`People/${personId}?loadAttributes=simple`);
  }

  async getBinaryFile(fileId: number) {
    return await this.get(`BinaryFiles/${fileId}`);
  }

  async getPrimaryFamily(primaryFamilyId: number) {
    return await this.get(`Groups/GetFamily/${primaryFamilyId}`);
  }

  async getCampus(campusId: number) {
    return await this.get(`Campuses/${campusId}`);
  }

  async getDefinedValue(id: number) {
    return await this.get(`DefinedValues/${id}`);
  }

  async getDefinedValuesByType(definedTypeId: number) {
    return await this.get(`DefinedValues?$filter=DefinedTypeId%20eq%20${definedTypeId}`);
  }

  async setAttributeValue(id: number, attributeKey: string, attributeValue: string) {
    return await this.post(`People/AttributeValue/${id}?attributeKey=${attributeKey}&attributeValue=${attributeValue}`);
  }

  async getConnectionRequestActivities(connectionRequestId: number) {
    const connectionRequestActivities = await this.get(`ConnectionRequestActivities?$filter=ConnectionRequestId%20eq%20${connectionRequestId}&loadAttributes=simple`);
    if (connectionRequestActivities) {
      for (const connectionRequestActivity of connectionRequestActivities) {
        connectionRequestActivity.ConnectionActivityType = await this.getConnectionActivityType(connectionRequestActivity.ConnectionActivityTypeId);
      }
    }
    return connectionRequestActivities;
  }

  async getConnectionActivityType(connectionActivityTypeId: number) {
    return await this.get(`ConnectionActivityTypes/${connectionActivityTypeId}`);
  }

  async getConnectionStatus(connectionStatusId: number) {
    return await this.get(`ConnectionStatus/${connectionStatusId}`);
  }

}

    export interface LoginParameters {
        Username?: string;
        Password?: string;
        Authorization?: string;
        Persisted?: boolean;
    }

    export enum ConnectionState {
        /// <summary>
        /// Active
        /// </summary>
        Active = 0,
    
        /// <summary>
        /// Inactive
        /// </summary>
        Inactive = 1,
    
        /// <summary>
        /// Future Follow-up
        /// </summary>
        FutureFollowUp = 2,
    
        /// <summary>
        /// Connected
        /// </summary>
        Connected = 3,
    }

    export enum Gender {
        /// <summary>
        /// Unknown
        /// </summary>
        Unknown = 0,

        /// <summary>
        /// Male
        /// </summary>
        Male = 1,

        /// <summary>
        /// Female
        /// </summary>
        Female = 2
    }

    export enum EmailPreference {
        /// <summary>
        /// Emails can be sent to person
        /// </summary>
        EmailAllowed = 0,

        /// <summary>
        /// No Mass emails should be sent to person
        /// </summary>
        NoMassEmails = 1,

        /// <summary>
        /// No emails should be sent to person
        /// </summary>
        DoNotEmail = 2
    }

    export enum AgeClassification {
        /// <summary>
        /// Unknown
        /// </summary>
        Unknown = 0,

        /// <summary>
        /// Adult
        /// </summary>
        Adult = 1,

        /// <summary>
        /// Child
        /// </summary>
        Child = 2
    }

    export enum AgeBracket {
        /// <summary>
        /// Unknown Age range
        /// </summary>
        /// [Description( "Unknown" )]
        Unknown = 0,

        /// <summary>
        /// Age range 0 - 5
        /// </summary>
        /// 0 - 12 was later split into 0-5 and 6-12 hence the irregular enum value.
        /// [Description( "0 - 5" )]
        ZeroToFive = 1,

        /// <summary>
        /// Age range 6 - 12
        /// </summary>
        /// [Description( "6 - 12" )]
        SixToTwelve = 2,

        /// <summary>
        /// Age range 13 - 17
        /// </summary>
        /// [Description( "13 - 17" )]
        ThirteenToSeventeen = 3,

        /// <summary>
        /// Age range 18 - 24
        /// </summary>
        /// [Description( "18 - 24" )]
        EighteenToTwentyFour = 4,

        /// <summary>
        /// Age range 25 - 34
        /// </summary>
        /// [Description( "25 - 34" )]
        TwentyFiveToThirtyFour = 5,

        /// <summary>
        /// Age range 35 - 44
        /// </summary>
        /// [Description( "35 - 44" )]
        ThirtyFiveToFortyFour = 6,

        /// <summary>
        /// Age range 45 - 54
        /// </summary>
        /// [Description( "45 - 54" )]
        FortyFiveToFiftyFour = 7,

        /// <summary>
        /// Age range 55 - 64
        /// </summary>
        /// [Description( "55 - 64" )]
        FiftyFiveToSixtyFour = 8,

        /// <summary>
        /// Age range 65+
        /// </summary>
        /// [Description( "65+" )]
        SixtyFiveOrOlder = 9
    }